import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Info, ArrowRight, RefreshCw, AlertTriangle } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const SEV = {
  MATERIAL: "bg-rose-500/15 text-rose-700 border-rose-200",
  ATTENTION: "bg-amber-500/15 text-amber-700 border-amber-200",
  INFORMATIONAL: "bg-slate-500/10 text-slate-600 border-slate-200",
};
const STATUS_TONE = {
  AVAILABLE: "text-emerald-600", INCOMPLETE: "text-amber-600", STALE: "text-amber-600",
  FAILED: "text-rose-600", NO_DATA: "text-slate-400", NOT_SUPPORTED: "text-slate-400",
};
const DS_LABEL = { customers: "Customers", pets: "Pets", bookings: "Bookings", services: "Services",
  inventory: "Inventory", veterinary: "Veterinary", boarding: "Boarding", training: "Training",
  finance: "Finance", geography: "Geography" };

export default function DataQuality() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const enabled = !!user?.server_flags?.data_quality;
  const load = () => {
    setLoading(true);
    api.get("/data-quality").then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e)).finally(() => setLoading(false));
  };
  useEffect(() => { if (enabled) load(); else setLoading(false); }, [enabled]);
  if (!enabled) {
    return (
      <div className="mx-auto max-w-3xl" data-testid="tenant-data-quality">
        <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="dq-disabled">
          <ShieldCheck className="mx-auto h-6 w-6 text-slate-300" />
          <div className="mt-2 font-medium text-slate-700">Data Quality isn’t available</div>
          <div className="mt-1 text-sm text-slate-500">This feature is currently turned off for your workspace.</div>
        </div>
      </div>
    );
  }
  const findings = (data?.findings || []).filter((f) => f.status === "INCOMPLETE" || f.status === "STALE" || f.status === "FAILED");
  return (
    <div className="mx-auto max-w-5xl" data-testid="tenant-data-quality">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Data Quality</h1>
          <p className="text-sm text-slate-500">Records that are missing information. Fix them in your normal screens — nothing here changes data directly.</p>
        </div>
      </div>
      {data?.branch_scoped && <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="dq-branch-note">Showing your branch scope only.</div>}
      {error ? (
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-6 text-center" data-testid="dq-error">
          <AlertTriangle className="mx-auto h-6 w-6 text-rose-500" />
          <div className="mt-2 text-sm text-rose-700">{apiErr(error)}</div>
          <button onClick={load} className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm"><RefreshCw className="h-4 w-4" /> Retry</button>
        </div>
      ) : loading || !data ? <div className="mt-8 text-slate-400" data-testid="dq-loading">Loading…</div>
        : findings.length === 0 ? (
          <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="dq-empty">
            <Info className="mx-auto h-6 w-6 text-slate-300" />
            <div className="mt-2 font-medium text-slate-700">No incomplete records found</div>
            <div className="mt-1 text-sm text-slate-500">Your operational data looks complete for the current checks.</div>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{["Area", "Issue", "Affected", "Severity", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
              <tbody>{findings.map((f, i) => (
                <tr key={i} data-testid={`dq-row-${f.dataset}-${f.issue_type}`} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-800">{DS_LABEL[f.dataset] || f.dataset}</td>
                  <td className="px-4 py-3 text-slate-600">{f.issue_type.replace(/_/g, " ")} <span className="text-slate-400">({f.field})</span></td>
                  <td className="px-4 py-3">{fmt(f.affected_count)} / {fmt(f.total_count)} ({f.affected_percentage}%)</td>
                  <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${SEV[f.severity]}`}>{f.severity}</span></td>
                  <td className={`px-4 py-3 text-xs ${STATUS_TONE[f.status] || ""}`}>{f.status}</td>
                  <td className="px-4 py-3 text-right">
                    {f.fix_route && <button data-testid={`dq-fix-${f.dataset}-${f.issue_type}`} onClick={() => navigate(f.fix_route)} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">Open {DS_LABEL[f.dataset] || f.dataset} <ArrowRight className="h-3.5 w-3.5" /></button>}
                  </td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      <div className="mt-4 text-xs text-slate-400">Descriptive completeness only — this is not a business-quality score. Corrections use your existing edit screens with the usual permissions and validation.</div>
    </div>
  );
}
